import { type ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  assertedGestationalAgeOf,
  eddOf,
  formatDatingDate,
  formatGestationalAge,
  lmpOf,
  parseDatingDate,
  PREGNANCY_DATING_BASIS,
  type PregnancyDating,
  type PregnancyDatingBasis,
} from '@gene42/pedigree/pregnancy-dating';

import { DateInput } from '@/components/ui/date-input';
import { formatDate } from '@/lib/format';

import { GestationalAgeInput } from './GestationalAgeInput';

/**
 * Pregnancy dating inputs (PTV2-1586) — the one control for LMP / EDD / GA, shared by the patient
 * record's Personal-info card and the pedigree node-menu field so the two can never drift.
 *
 * All three are views of ONE stored anchor date, which carries a basis flag recording which of them
 * the clinician actually asserted — an ultrasound-revised EDD is a new anchor that no longer equals
 * LMP + 280 days. Editing any field re-anchors dating on it and replaces the basis. A GA is entered
 * with the date it was measured on and stored as the LMP it implies, never as a number.
 */
export interface PregnancyDatingInputsProps {
  /** Prefix for the generated input ids, so two instances can coexist on a page. */
  readonly idPrefix: string;
  readonly value: PregnancyDating | null;
  readonly disabled?: boolean | undefined;
  /** Receives the new anchor+basis pair, or null when the clinician clears dating. */
  readonly onChange: (next: PregnancyDating | null) => void;
}

export function PregnancyDatingInputs({
  idPrefix,
  value,
  disabled,
  onChange,
}: PregnancyDatingInputsProps): ReactElement {
  const { t } = useTranslation();

  const dating = value ?? null;
  const basis = dating?.basis ?? null;

  const commit = useCallback(
    (iso: string, nextBasis: PregnancyDatingBasis) => {
      // A cleared date clears dating outright rather than leaving a stale anchor behind under a basis
      // the clinician is no longer asserting. A partial date (year or year-month) is still being
      // typed and changes nothing yet.
      if (iso === '') {
        onChange(null);
        return;
      }
      const anchor = parseDatingDate(iso);
      if (anchor !== null) onChange({ anchor, basis: nextBasis });
    },
    [onChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <DatingDateField
          id={`${idPrefix}-lmp`}
          label={t('patient.demographics.lmp', { defaultValue: 'LMP' })}
          value={formatDatingDate(lmpOf(dating)) ?? ''}
          derived={basis !== null && basis !== PREGNANCY_DATING_BASIS.LMP}
          disabled={disabled}
          onChange={(iso) => {
            commit(iso, PREGNANCY_DATING_BASIS.LMP);
          }}
        />
        <DatingDateField
          id={`${idPrefix}-edd`}
          label={t('patient.demographics.edd', { defaultValue: 'EDD' })}
          value={formatDatingDate(eddOf(dating)) ?? ''}
          derived={basis !== null && basis !== PREGNANCY_DATING_BASIS.EDD}
          disabled={disabled}
          onChange={(iso) => {
            commit(iso, PREGNANCY_DATING_BASIS.EDD);
          }}
        />
      </div>
      <GestationalAgeInput idPrefix={idPrefix} dating={dating} disabled={disabled} onChange={onChange} />
      <DatingBasisLine dating={dating} />
    </div>
  );
}

/** "Dated from LMP" / "Dated from EDD" / "Dated from GA 12w 3d on Sep 15, 2026". */
function DatingBasisLine({ dating }: { readonly dating: PregnancyDating | null }): ReactElement | null {
  const { t } = useTranslation();
  if (dating?.anchor == null) return null;
  const asserted = formatGestationalAge(assertedGestationalAgeOf(dating));
  const asOf = formatDatingDate(dating.asOf ?? null);
  const text = dating.basis === PREGNANCY_DATING_BASIS.GA && asserted !== null && asOf !== null
    ? t('patient.demographics.datedFromGa', {
        defaultValue: 'Dated from GA {{ga}} on {{date}}',
        ga: asserted,
        date: formatDate(asOf),
      })
    : t('patient.demographics.datedFrom', {
        defaultValue: 'Dated from {{basis}}',
        basis: dating.basis === PREGNANCY_DATING_BASIS.EDD ? 'EDD' : 'LMP',
      });
  return <p className="text-xs text-muted-foreground">{text}</p>;
}

interface DatingDateFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** True when this date was computed from the other one rather than asserted by the clinician. */
  readonly derived: boolean;
  readonly disabled?: boolean | undefined;
  readonly onChange: (iso: string) => void;
}

function DatingDateField({ id, label, value, derived, disabled, onChange }: DatingDateFieldProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
        {derived && (
          <span className="ml-1 font-normal opacity-70">
            {t('patient.demographics.datingDerived', { defaultValue: '(derived)' })}
          </span>
        )}
      </label>
      <DateInput id={id} value={value} onChange={onChange} disabled={disabled ?? false} />
    </div>
  );
}
